import type { MuiTheme as Theme } from '@paintscout/ui'
import { makeStyles } from '@paintscout/ui'
import { useTrialDashboardMobileTypographyStyles } from '../../shared/useTrialDashboardMobileTypographyStyles'

const FLEX_START = 'flex-start'

const useLayoutStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2, 0),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    position: 'relative',
    overflowX: 'visible',
    width: '100%',
    minHeight: 'calc(100vh - 200px)',
    justifyContent: FLEX_START,
    alignItems: 'center',
    paddingTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 0),
      gap: theme.spacing(2),
      paddingTop: theme.spacing(2)
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2, 0),
      gap: theme.spacing(3),
      paddingTop: theme.spacing(3)
    }
  },
  leftSection: {
    padding: theme.spacing(2, 1),
    width: '100%',
    order: 1,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 0.5)
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2, 1)
    }
  },
  whiteBackground: {
    width: '100vw',
    maxWidth: '100vw',
    height: 'auto',
    minHeight: '202px',
    position: 'relative',
    marginLeft: `calc((100vw - 100%) / -2)`,
    marginRight: `calc((100vw - 100%) / -2)`,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
    order: 2,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 0,
    border: '1.19px solid #D9DEE8',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    boxSizing: 'border-box',
    [theme.breakpoints.down('xs')]: {
      minHeight: '152px',
      padding: 0,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(3)
    },
    [theme.breakpoints.up('sm')]: {
      minHeight: '202px',
      padding: 0
    }
  },
  rightSection: {
    position: 'absolute',
    width: '100%',
    minHeight: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    order: 0,
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'auto'
    }
  },
  couponContainer: {
    maxWidth: '100%',
    width: '100%',
    height: '100%',
    minHeight: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0
  },
  couponBackground: {
    position: 'absolute',
    maxWidth: '260px',
    height: '260px',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
    pointerEvents: 'auto',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '220px',
      height: '220px'
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: '260px',
      height: '260px'
    }
  },
  couponCard: {
    position: 'absolute',
    maxWidth: '280px',
    height: '160px',
    padding: theme.spacing(1.5, 2),
    borderRadius: '16px',
    transform: 'translate(calc(-50% + 43px), -50%)',
    zIndex: 2,
    width: '85%',
    border: '1.103px solid #B4BED0',
    backgroundColor: '#EBEFF5',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: theme.shadows[8],
    overflow: 'hidden',
    top: '50%',
    left: '50%',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '240px',
      height: '135px',
      padding: theme.spacing(1, 1.5),
      borderRadius: '14px',
      transform: 'translate(calc(-50% + 38px), -50%)'
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: '280px',
      height: '160px',
      transform: 'translate(calc(-50% + 43px), -50%)'
    }
  },
  sherwinLogo: {
    display: 'flex',
    alignItems: FLEX_START,
    justifyContent: FLEX_START,
    marginBottom: theme.spacing(0.25),
    marginTop: theme.spacing(-1),
    marginLeft: theme.spacing(-1),
    flexShrink: 0,
    width: '100%'
  },
  logoImage: {
    maxWidth: '100%',
    height: 'auto',
    objectFit: 'contain',
    display: 'block',
    [theme.breakpoints.down('xs')]: {
      maxHeight: '60px'
    },
    [theme.breakpoints.up('sm')]: {
      maxHeight: '70px'
    }
  },
  couponContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    minHeight: 0,
    overflow: 'visible',
    gap: 0,
    marginTop: theme.spacing(-2)
  },
  couponAmount: {
    color: '#383A36',
    fontFamily: 'Euclid',
    fontSize: '60px',
    fontWeight: 600,
    lineHeight: '1',
    letterSpacing: '-1.8px',
    textAlign: 'center',
    width: '100%',
    flexShrink: 0,
    margin: 0,
    marginBottom: theme.spacing(-1),
    [theme.breakpoints.down('xs')]: {
      fontSize: '50px',
      letterSpacing: '-1.5px',
      lineHeight: '1',
      marginBottom: theme.spacing(-1)
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '60px',
      letterSpacing: '-1.8px',
      lineHeight: '1',
      marginBottom: theme.spacing(-1)
    }
  },
  couponLabel: {
    fontSize: '24px',
    width: 'auto',
    color: '#373A36',
    textAlign: 'center',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: '-0.48px',
    margin: 0,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    minWidth: '120px',
    [theme.breakpoints.down('xs')]: {
      fontSize: '20px',
      letterSpacing: '-0.4px',
      minWidth: '100px'
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '24px',
      letterSpacing: '-0.48px',
      minWidth: '120px'
    }
  }
}))

export const useSherwinTrialOverDashboardMobileLayoutStyles = () => {
  const layoutStyles = useLayoutStyles()
  const typographyStyles = useTrialDashboardMobileTypographyStyles()
  return { ...layoutStyles, ...typographyStyles }
}
